import {svg} from '../svg.ts';
import {getComboMarkdownEditor} from './comp/ComboMarkdownEditor.ts';

// Snippet markdown (`!thumbnail[](...){...}`) is kept in labels above the
// comment textarea so the user can't delete it by accident. It is merged back
// into `content` on submit and on preview, so the server format is unchanged.
// The labels live in the form's DOM, so cancelling the form drops them too.

const containerSelector = '.attached-snippet-container';
const snippetSelector = '.attached-snippet';

// A single space keeps the thumbnails and the text in one paragraph, which is
// how snippet comments have always been stored.
export function mergeSnippetContent(snippets: string[], text: string): string {
  return [...snippets, text].filter(Boolean).join(' ');
}

export function snippetLabelFromPath(path: string, fallback: string): string {
  const name = path.split('/').pop() ?? '';
  return name || fallback;
}

export function getAttachedSnippets(form: HTMLFormElement): string[] {
  const labels = form.querySelectorAll<HTMLElement>(snippetSelector);
  return Array.from(labels, (label) => label.getAttribute('data-snippet-markdown'));
}

export function hasSnippet(form: HTMLFormElement): boolean {
  return getAttachedSnippets(form).length > 0;
}

function mergeFormContent(form: HTMLFormElement, text: string): string {
  return mergeSnippetContent(getAttachedSnippets(form), text);
}

export function attachSnippet(form: HTMLFormElement, snippet: string, path: string): void {
  const container = form.querySelector<HTMLElement>(containerSelector);
  if (!container) {
    throw new Error(`Couldn't find ${containerSelector} in the comment form`);
  }

  if (!getAttachedSnippets(form).includes(snippet)) {
    const prefix = container.getAttribute('data-snippet-label') || 'Snippet';
    const fileName = snippetLabelFromPath(path, '');

    const label = document.createElement('span');
    label.className = 'ui small basic label attached-snippet';
    label.setAttribute('data-snippet-markdown', snippet);
    if (path) {
      label.title = path;
    }
    label.innerHTML = svg('octicon-image');
    label.append(` ${fileName ? `${prefix}: ${fileName}` : prefix}`);
    container.append(label);
  }

  const textarea = form.querySelector<HTMLTextAreaElement>('textarea.markdown-text-editor');
  if (textarea) {
    textarea.required = false;
  }

  const editor = getComboMarkdownEditor(textarea);
  if (editor) {
    editor.options.previewTextTransform = (text: string) => mergeFormContent(form, text);
  }

  // `formdata` fires from `new FormData(form)`, so this covers button submit and Ctrl/Cmd+Enter.
  if (form.getAttribute('data-attached-snippet-bound') !== 'true') {
    form.setAttribute('data-attached-snippet-bound', 'true');
    form.addEventListener('formdata', (e: FormDataEvent) => {
      const text = String(e.formData.get('content') ?? '');
      e.formData.set('content', mergeFormContent(form, text));
    });
  }
}
